// Launch round 1 (Lane 1440, Ryan's mockup v1): a short confirmation in place of the long
// Review and publish panel. It first waits for every save the buyer made by leaving a
// field (a failed one stops it), then reads the Overview once and shows those values; the
// launch sends exactly them, with that read's revision, to the existing publish action
// (same checks, receipts, notices and emails). Anything changed after the read is refused
// as stale and the dialog reads again.
import React, { useCallback, useEffect, useState } from 'react';
import { STALE_MESSAGE, endSentence } from './sourcingApi';
import InvitationLinks, { DELIVERY_TEXT } from './InvitationLinks';
import { CheckList } from './PublishReview';
import { offersDueLocal, offersDueText } from './RfpSetupCard';
import { viewerZone } from './useSourcingChat';
import useDialogFocus from './useDialogFocus';
import { saveBlob } from './DocumentsTab';
import { EventHeader, EventDetails, PartsTable, formatDueAt } from '../sourcing-portal/portalView';

// How far a zone's clock is ahead of UTC at an instant, in ms.
function zoneOffsetMs(date, zone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date);
  const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour % 24, +v.minute, +v.second) - date.getTime();
}

// A day and time of day on a zone's clock, as an instant (two passes: right across a
// daylight saving change).
export function wallTimeIn(day, time, zone) {
  const [y, m, d] = String(day).slice(0, 10).split('-').map(Number);
  const [hh, mm] = String(time).split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  try {
    let t = guess - zoneOffsetMs(new Date(guess), zone);
    t = guess - zoneOffsetMs(new Date(t), zone);
    return new Date(t);
  } catch (e) {
    return null;
  }
}

// The deadline Launch sends (Lane 1442, Ryan 2026-11-05): the saved "Offers due" day
// and time of day (5:00 PM when no time is set) on the clock of the zone the buyer
// picks here, never simply the clock of whoever presses Launch. Without a zone: as
// before (the saved instant, else the saved day at 5 PM here).
function launchDue(setup, zone) {
  const due = setup && setup.offers_due;
  if (zone && due && due.due_date) {
    return wallTimeIn(due.due_date, due.due_time || '17:00', zone);
  }
  const local = offersDueLocal(due);
  const d = local ? new Date(local) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

// The zones the buyer can pick for the deadline: the US zones, then any of the
// company's, the event's and the buyer's own that are not already listed.
const ZONES = [
  ['America/New_York', 'US Eastern'], ['America/Chicago', 'US Central'],
  ['America/Denver', 'US Mountain'], ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'US Pacific'], ['Europe/London', 'UK'], ['UTC', 'UTC'],
];

function zoneOptions(extra) {
  const out = [...ZONES];
  extra.filter(Boolean).forEach((z) => {
    if (!out.some(([id]) => id === z)) out.push([z, z.replace(/_/g, ' ')]);
  });
  return out;
}

function firstZone(setup) {
  const z = (setup && setup.time_zones) || {};
  return z.company || z.event || viewerZone() || 'UTC';
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function names(list) {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const MISSING_TEXT = {
  offers_due: 'Set when offers are due (Overview, RFP setup).',
  offers_due_passed: 'Offers due has passed or is less than 5 minutes away. Choose a later time.',
  no_invitees: 'Every supplier declined. Add a supplier to invite.',
};

function checks(launch, setup, emailEnabled, due, zone) {
  const out = [];
  const n = launch.of;
  if (launch.no_email.length) {
    out.push(['block', `${names(launch.no_email)} ${launch.no_email.length === 1 ? 'has' : 'have'} no valid email yet.`]);
  } else if (emailEnabled) {
    out.push(['ok', `${n === 1 ? 'The supplier gets' : `All ${n} suppliers get`} the Wingman email with their login and bid steps.`]);
  } else {
    out.push(['warn', 'Email is off. After you launch, you copy each supplier\'s link and send it yourself.']);
  }
  if (launch.email_conflicts.length) {
    out.push(['block', `${names(launch.email_conflicts)}: the email already works with you for another supplier.`]);
  }
  out.push(setup.offers_due && setup.offers_due.due_date
    ? ['ok', `Offers due ${due ? formatDueAt(due.toISOString(), zone) : offersDueText(setup.offers_due)}`]
    : ['block', 'Offers due is not set.']);
  out.push(['ok', `${plural(launch.parts, 'part')} at ${plural(launch.sites, 'site')}`]);
  if (launch.confirmed === n) out.push(['ok', n === 1 ? 'The supplier confirmed.' : `Every supplier confirmed (${n}).`]);
  else out.push(['warn', `${launch.confirmed} of ${n} confirmed. The others still get the email.`]);
  if (launch.declined.length) out.push(['warn', `${names(launch.declined)} declined and ${launch.declined.length === 1 ? 'is' : 'are'} not invited.`]);
  if (launch.title_flag) {
    const named = (launch.title_names || []).length ? ` (${launch.title_names.join(', ')})` : '';
    out.push(['warn', `The title names one of your suppliers${named}. Every supplier will see it.`]);
  }
  launch.missing.filter((m) => m.message || MISSING_TEXT[m.code]).forEach((m) => {
    if (m.code !== 'offers_due') out.push(['block', m.message || MISSING_TEXT[m.code]]);
  });
  return out;
}

// What each supplier gets at launch (Lane 1440, Ryan): the invitation email exactly as
// publish builds it (only the personal link differs per supplier), and the bid guide.
// Lane 1442: Wingman fills these in for each supplier; [link] must stay (once).
const INVITE_LINK = '[link]';

function WhatSuppliersReceive({ api, eventId, mail, emailOn, onSaved }) {
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);         // the text being edited, or null
  const [saving, setSaving] = useState(false);
  const guide = () => api.bidGuide(eventId)
    .then((b) => saveBlob(b, 'How to submit your bid.pdf')).catch((e) => setError(e.message));
  const save = async (text) => {
    setError('');
    if (text !== '' && text.split(INVITE_LINK).length !== 2) {
      setError(`Keep ${INVITE_LINK} once in the email: it is where each supplier's own link goes.`);
      return;
    }
    setSaving(true);
    try {
      await api.savePublishDraft(eventId, { invite_email_body: text });
      setDraft(null);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  if (!mail) return null;
  return (
    <section className="srce-inv-col" aria-label="What suppliers receive" data-testid="srce-launch-email">
      <span className="srce-row">
        <span className="srce-strong">What suppliers receive</span>
        {draft === null && mail.template && (
          <button type="button" className="ll-clr" onClick={() => { setError(''); setDraft(mail.template); }}>Edit email</button>
        )}
      </span>
      <span className="srce-mut">{endSentence(`${emailOn ? 'This email'
        : 'Email is off: you send each supplier their link and this text yourself'}${mail.to ? `, shown for ${mail.to.supplier_name}` : ''}`)}</span>
      {draft !== null ? (
        <>
          <span className="srce-mut">Wingman fills in [contact], [due date] and [link] for each supplier. Keep [link].</span>
          <textarea className="srce-input srce-invite-edit" rows={18} value={draft} maxLength={6000}
            aria-label="Invitation email text" disabled={saving} onChange={(e) => setDraft(e.target.value)} />
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary" disabled={saving} onClick={() => save(draft)}>
              {saving ? 'Saving…' : 'Save email'}</button>
            <button type="button" className="srce-btn" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>Cancel</button>
            {mail.edited && (
              <button type="button" className="ll-clr" disabled={saving} onClick={() => save('')}>Use Wingman's text</button>
            )}
          </span>
        </>
      ) : (
        <pre className="srce-guide-email">{`${mail.from ? `From: ${mail.from.replace(/"/g, '')}\n` : ''}${mail.reply_to ? `Reply-To: ${mail.reply_to}\n` : ''}Subject: ${mail.subject}\n\n${mail.body}`}</pre>
      )}
      <span className="srce-row">
        {(mail.attachments || []).length > 0 && <span className="srce-mut">Attached: {mail.attachments.join(', ')}</span>}
        <button type="button" className="ll-clr" onClick={guide}>Bid guide (PDF)</button>
      </span>
      {error && <span className="srce-error" role="alert">{error}</span>}
    </section>
  );
}

function Preview({ api, eventId, title, shareSites, onBack }) {
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    api.publishReview(eventId, { supplier_title: title, share_sites: shareSites, preview_page: page })
      .then((r) => { if (live) setPreview(r.preview); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [api, eventId, title, shareSites, page]);
  return (
    <div className="srce-inv-col">
      <span className="srce-row"><button type="button" className="ll-clr" onClick={onBack}>Back</button>
        <span className="srce-strong">What suppliers see</span><span className="srce-mut">Read only</span></span>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {!preview && !error && <div className="ll-loading">Loading…</div>}
      {preview && (
        <div className="srce-preview-box">
          <EventHeader untitled="No title yet" event={{ ...preview.event, round: preview.event.round || 1 }} />
          <EventDetails event={preview.event} />
          {preview.parts && preview.parts.items.length > 0 && (
            <>
              <div className="srce-mut srce-gap srce-row">
                Page {preview.parts.page} of {preview.parts.total_pages} ({preview.parts.total.toLocaleString()} parts).
                <button type="button" className="srce-btn" disabled={preview.parts.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
                <button type="button" className="srce-btn" disabled={preview.parts.page >= preview.parts.total_pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
              <PartsTable items={preview.parts.items} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function LaunchDialog({ api, eventId, saveQueue, onClose, onLaunched, onGoInvitations }) {
  const [snap, setSnap] = useState(null);           // {revision, setup, launch, email}
  const [error, setError] = useState('');
  const [blocks, setBlocks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareSites, setShareSites] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [zone, setZone] = useState(null);           // the deadline's zone (Lane 1442)
  const [mail, setMail] = useState(null);           // the email as it goes out in that zone
  const [mailTick, setMailTick] = useState(0);      // read the email again after an edit
  const box = useDialogFocus(busy ? () => {} : onClose);

  const read = useCallback(async () => {
    setSnap(null);
    setError('');
    // Every save the buyer made first; a failed one stops here (Codex plan r1 #3).
    if (!(await saveQueue.flush())) {
      setError(`Some details are not saved: ${saveQueue.error || 'check your connection'}. Fix them, then launch.`);
      return;
    }
    try {
      const [ov, review] = await Promise.all([api.overview(eventId), api.invitationLetter(eventId)]);
      if (!ov.invitations || !ov.invitations.launch) { setError('This event is not a draft any more.'); return; }
      // The deadline's zone starts at the company's own (then the event's, then the
      // buyer's); the email below follows the zone picked (one rule, launchDue).
      setZone((z) => z || firstZone(ov.invitations.setup));
      setSnap({ revision: ov.revision, setup: ov.invitations.setup, launch: ov.invitations.launch,
        email: review.email_enabled, letterNames: review.names_supplier || [] });
      setShareSites(Boolean(ov.invitations.launch.share_sites));
    } catch (e) {
      setError(e.message);
    }
  }, [api, eventId, saveQueue]);
  useEffect(() => { read(); }, [read]);

  // The email with the deadline and zone this dialog will send, read again when the
  // buyer picks another zone.
  useEffect(() => {
    if (!snap || !zone) return undefined;
    let live = true;
    const due = launchDue(snap.setup, zone);
    api.invitationEmailPreview(eventId, { ...(due ? { due_at: due.toISOString() } : {}), time_zone: zone })
      .then((m) => { if (live) setMail(m); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [api, eventId, snap, zone, mailTick]);

  const launch = async () => {
    const { setup, launch: l, revision } = snap;
    const due = launchDue(setup, zone);
    setBusy(true);
    setError('');
    setBlocks(null);
    try {
      const r = await api.publish(eventId, {
        supplier_title: setup.title, due_at: due.toISOString(), share_sites: shareSites,
        ...(zone ? { time_zone: zone } : {}),
        contacts: l.invitees.map((s) => ({ supplier_id: s.supplier_id, email: s.email.trim(),
          ...(s.name && s.name.trim() ? { name: s.name.trim() } : {}) })),
      }, revision);
      setResult({ ...r, names: Object.fromEntries(l.invitees.map((s) => [s.supplier_id, s.supplier_name])) });
      await onLaunched();
    } catch (e) {
      if (e.code === 'publish_blocked') {
        setBlocks((e.detail && e.detail.blocks) || []);
        setError(e.message);
      } else if (e.code === 'stale_revision') {
        setError(STALE_MESSAGE);
        await read();
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (result) {
    const inv = (result.result && result.result.invitations) || [];
    const delivery = result.delivery || {};
    const due = formatDueAt(result.result && result.result.due_at);
    body = (
      <>
        <div className="srce-good">Round 1 is open{due ? ` until ${due}` : ''}. The parts in this RFP can no longer change.</div>
        {result.links && result.links.length > 0 ? <InvitationLinks links={result.links} names={result.names} /> : (
          <ul className="srce-plainlist">
            {inv.map((i) => (
              <li key={i.invitation_id}><span className="srce-strong">{i.supplier_name}</span>, {i.email}:{' '}
                {DELIVERY_TEXT[delivery[i.invitation_id] || i.delivery] || 'Invitation saved'}</li>
            ))}
          </ul>
        )}
      </>
    );
  } else if (previewing && snap) {
    body = <Preview api={api} eventId={eventId} title={snap.setup.title} shareSites={shareSites} onBack={() => setPreviewing(false)} />;
  } else if (!snap) {
    body = error ? null : <div className="ll-loading">Checking the RFP…</div>;
  } else {
    const due = launchDue(snap.setup, zone);
    const items = checks(snap.launch, snap.setup, snap.email, due, zone);
    // What Review and publish showed under "Check these" (Codex code r1 #2), and a letter
    // that names a supplier (the letter goes out before launch, so it is only a warning).
    (snap.launch.warnings || []).forEach((x) => items.push(['warn', x.message]));
    if (snap.letterNames.length) {
      items.push(['warn', `The RFP invitation letter names one of your suppliers (${snap.letterNames.join(', ')}).`]);
    }
    body = (
      <>
        <div className="srce-mut">&ldquo;{snap.setup.title}&rdquo;</div>
        <ul className="srce-launch-list">
          {items.map(([tone, text], i) => (
            <li key={i} className={`srce-launch-i srce-launch-i--${tone}`}>
              <span className="srce-launch-ic" aria-hidden="true">{tone === 'ok' ? '✓' : '!'}</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <label className="srce-inline">
          Time zone for the deadline
          <select value={zone || ''} disabled={busy} onChange={(e) => setZone(e.target.value)}
            aria-label="Time zone for the deadline">
            {zoneOptions([zone, snap.setup.time_zones && snap.setup.time_zones.company,
              snap.setup.time_zones && snap.setup.time_zones.event, viewerZone()]).map(([id, label]) => (
              <option key={id} value={id}>{label}{due ? ` (${formatDueAt(due.toISOString(), id).split(' ').pop()})` : ''}</option>
            ))}
          </select>
        </label>
        <WhatSuppliersReceive api={api} eventId={eventId} mail={mail} emailOn={snap.email}
          onSaved={() => setMailTick((t) => t + 1)} />
        <label className="srce-inline">
          <input type="checkbox" checked={shareSites} disabled={busy} onChange={(e) => setShareSites(e.target.checked)} />
          Share site names with suppliers
        </label>
        {blocks && blocks.length > 0 && <CheckList items={blocks} tone="block" />}
      </>
    );
  }
  const ready = snap && snap.launch.ready && !busy;
  return (
    <div className="srce-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={`srce-modal ${previewing ? '' : 'srce-modal--narrow'}`} role="dialog" aria-modal="true"
        aria-label={result ? 'Round 1 launched' : 'Launch round 1'} ref={box}>
        <div className="srce-card2-h"><span className="srce-card2-t">{result ? 'Round 1 launched' : 'Launch round 1?'}</span></div>
        <div className="srce-modal-b srce-launch-b">
          {error && <div className="srce-error" role="alert">{error}</div>}
          {body}
        </div>
        <div className="srce-modal-f">
          {result ? (
            <span className="srce-push srce-row">
              <button type="button" className="srce-btn" onClick={() => onGoInvitations()}>Go to invitations</button>
              <button type="button" className="srce-btn srce-btn--primary" onClick={onClose}>Close</button>
            </span>
          ) : (
            <>
              {snap && !previewing && <button type="button" className="ll-clr" onClick={() => setPreviewing(true)}>Preview what suppliers see</button>}
              <span className="srce-push srce-row">
                <button type="button" className="srce-btn" disabled={busy} onClick={onClose}>Not yet</button>
                <button type="button" className="srce-btn srce-btn--primary" disabled={!ready} onClick={launch}>
                  {busy ? 'Launching…' : 'Launch round 1'}</button>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
